import { secondaryStyles } from "@/components/Form/InputFields/secondaryStyles";
import { DATE_FORMAT_TWO } from "@/constants";
import { formFieldError, InputFieldType } from "@/types";
import convertDateToTimezone from "@/utils/convertDateToTimezone";
import ensureTimezone from "@/utils/ensureTimezone";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/system";
import { DateTimePicker, DateTimePickerProps } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useController, UseControllerProps } from "react-hook-form";

export default function CustomDateTimePicker({
  name,
  control,
  label = null,
  error,
  datePickerProps,
  type = "primary",
  required = false,
  timezone,
  isLoading = false,
  isClearable = true,
}: CustomDateTimePickerProps) {
  const theme = useTheme();

  const { field } = useController({
    control,
    name,
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en"}>
      <DateTimePicker
        label={label}
        value={field.value ? field.value : null}
        onChange={(newValue) => {
          if (!newValue) return null;
          if (timezone) {
            const timezoneEnsuredValue = field.value
              ? newValue
              : ensureTimezone(newValue.format(DATE_FORMAT_TWO), timezone);
            const value = convertDateToTimezone(timezoneEnsuredValue, timezone);
            field.onChange(value ? value : null);
          } else {
            const value = dayjs.isDayjs(newValue) ? newValue : dayjs(newValue);
            field.onChange(value);
          }
        }}
        onAccept={(newValue) => {
          if (!newValue) field.onChange(null);
        }}
        renderInput={(params) => {
          const { inputProps, ...restParams } = params;
          const value = inputProps?.value;

          return (
            <TextField
              {...restParams}
              required={required}
              fullWidth
              helperText={error && typeof error === "string" ? error : ""}
              FormHelperTextProps={{
                error: true,
              }}
              sx={{
                ...secondaryStyles(type, theme, false, true),
              }}
              inputProps={{
                ...inputProps,
                value,
                readOnly: true,
              }}
            />
          );
        }}
        loading={isLoading}
        componentsProps={{
          actionBar: {
            actions: isClearable ? ["clear"] : [],
          },
        }}
        {...datePickerProps}
      />
    </LocalizationProvider>
  );
}

interface CustomDateTimePickerProps extends UseControllerProps {
  label?: string | null;
  error?: formFieldError;
  datePickerProps?: Partial<DateTimePickerProps<any, any>>;
  type?: InputFieldType;
  required?: boolean;
  disabled?: boolean;
  timezone?: string;
  isLoading?: boolean;
  isClearable?: boolean;
}
